import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import useDataFetch from '../hooks/useDataFetch';
import useDataSelect from '../hooks/useDataSelect';
import { getFields, searchFields } from '../utils/fields';
import { formatDateTime, formatCurrency } from '../utils/format';
import SearchForm from '../components/SearchForm';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import RelatedMenu from '../components/RelatedMenu';
import { getEstimateStatusOptions } from '../utils/estimateStatus';
import { Paper, Button, Link, Typography } from "@mui/material";
const EstimatesPage = ({ hasLimit, userRole }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const initialStatus = useMemo(() => {
    const raw = location.state?.filterStatus;
    if (!raw) return [];

    const arr = typeof raw === 'string'
      ? raw.split(',')
      : [raw];

    return arr.map(String);
  }, [location.state?.filterStatus]);

  const { dataset: statuses } = useDataSelect('/estimate/estimate-statuses');
  const statusOptions = useMemo(() => {
    if (!statuses?.length) return [];
    return getEstimateStatusOptions({
      hasLimit,
      userRole,
      master: statuses,
    });
  }, [statuses, hasLimit, userRole]);
  const {
    dataset,
    error,
    currentPage,
    totalPages,
    searchQuery,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
  } = useDataFetch('/estimate', { estimate_numbers: '', client_name: '', dates: '', status: initialStatus });

  // 行クリック時にページ遷移を行う処理
  const handleDetail = async (item) => {
    navigate(`/estimates/${item.estimate_id}`); // 見積詳細ページに遷移
  };

  // 検索フィールド定義
  const searchItems = useMemo(() => {
    const base = getFields(searchFields, ['dates', 'estimate_numbers', 'client_name']);

    return [
      ...base,
      {
        name: 'status',
        type: 'select',
        label: 'ステータス',
        options: statusOptions.map(item => ({
          value: item.value,
          label: item.label,
        })),
      },
    ];
  }, [statusOptions]);

  const columns = [
    {
      label: '見積日',
      accessor: 'estimate_date',
      format: (date) => formatDateTime(date),
      width: '140px',
    },
    {
      label: '見積番号',
      accessor: 'estimate_number',
      render: (item) => (
        <Link
          component="button"
          onClick={() => handleDetail(item)}>
          {item.estimate_number}
        </Link>
      ),
      width: '140px',
    },
    {
      label: 'ステータス',
      accessor: 'status_name',
      width: '140px',
    },
    {
      label: '金額',
      accessor: 'total_amount',
      render: (item) => `${formatCurrency(item.total_amount)}`,
      width: '140px',
    },
    {
      label: '会社名＋事業所名',
      accessor: 'client_name',
      render: (item) => (
        <Typography>
          {item.client_name} {item.branch_name}
        </Typography>
      ),
      width: '350px',
    },
    {
      label: '',
      render: (item) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleDetail(item)}>
          詳細
        </Button>
      ),
      width: '50px',
    },
  ];
  const links = [
    { path: '/estimates/new', label: '新規見積作成', icon: 'library_add' },
  ];

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">見積一覧</Typography>
      <div className='grid2-2_1'>
        {/* 検索フォーム */}
        <SearchForm
          searchQuery={searchQuery}
          searchFields={searchItems}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleClearSearch={handleClearSearch}
        />
        <RelatedMenu links={links} />
      </div>
      <Paper variant='section'>
        <TableView dataset={dataset} columns={columns} keyColumn="estimate_id" />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Paper>
    </>
  );
};


export default EstimatesPage;
